import { Vector2 } from "../../../common/vector2.js";
import { ownDaysInGame } from "../../../common/days_in_game.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { SafeAreas } from "../../safe_areas.js";
import { LeaderboardScreenPlayerInfo } from "./leaderboard_screen_player_info.js";
import { LeaderboardScreenPlayerInfoConfig } from "./leaderboard_screen_player_info_config.js";
import { LeaderboardScreenTabs } from "./leaderboard_screen_tabs.js";
import { LeaderboardTab } from "./leaderboard_tab.js";
import { Spinner } from "./spinner.js";
import { snapToDevicePx } from "../../dom_backend/dom_backend.js";
import { UIScrollableContainerCSS } from "../../common_ui_elements/scrollable_container_css.js";
import type { BoardUser } from "../../../api/leaderboard/leaderboard.dto.js";
import { getLeaderboardCurrentUserId } from "../../../api/leaderboard/leaderboard_player_context.js";
import { LeaderboardBoardService, LeaderboardCountryBoardService, type BoardSnapshot, type BoardSource } from "../../../api/leaderboard/leaderboard_board_service.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { DialogManager } from "../../dialog/dialog_manager.js";
import { OverlayManager } from "../../overlay/overlay_manager.js";
import { LeaderboardProfileDialog } from "../../dialog/leaderboard_profile_dialog.js";
import { AvatarUtils } from "../../../common/avatar_utils.js";
import { getFlagByCountryCode } from "../../../common/flag_utils.js";
import { createNullCanvasContext } from "../../dom_backend/null_canvas_context.js";
import { buildPlayerRows, playersKey, visibleRowRange } from "./leaderboard_rows.js";
import { LeaderboardJumpButton } from "./leaderboard_jump_button.js";
import { navBarLineClientY } from "../../navigation_block/navigation_bar_line.js";
import { LeaderboardRowsView } from "./leaderboard_rows_view.js";
import { acquireLeaderboardRowBackdrop, createLeaderboardRowBackdrop, prepareLeaderboardRowBackdrop } from "./leaderboard_row_backdrop.js";

/** Rows kept built above and below the viewport, so a fast fling never shows an empty slot. */
const ROWS_BUFFER = 2;
/**
 * QA override: "native" enables experimental browser scrolling. By default, the small row pool
 * and the scroll transform update together, so a fast fling cannot outrun row binding or raster.
 */
const SCROLL_MODE_KEY = "bb_leaderboard_scroll_mode";

function nativeScrollEnabled(): boolean
{
  try { return localStorage.getItem( SCROLL_MODE_KEY ) === "native"; }
  catch { return false; }
}

/** Rows whose avatars and flags the menu warms up: what the screen shows when it opens. */
const WARMUP_ROWS = 12;
/**
 * With no board to show, the spinner waits this long before it appears: the head of the board
 * usually lands sooner, and a spinner that blinks for a few frames looks like a glitch.
 */
const SPINNER_DELAY_MS = 400;
const SPINNER_FADE_MS = 150;
// "Down" / "Up" pill, centred horizontally and in the gap between the list's last card and the
// navigation line (`placeJumpButton`). The offset (its top, from the screen bottom) is only the
// start position until the bar's line is measured.
const JUMP_BUTTON_W_DESIGN = 320;
const JUMP_BUTTON_H_DESIGN = 64;
const JUMP_BUTTON_BOTTOM_OFFSET_DESIGN = 357;

const ITEM_H_DESIGN              = 160;
const ITEM_GAP_DESIGN            = 0;
const CARD_H_DESIGN              = 160;
const FLOATING_CARD_RENDER_OVERFLOW_DESIGN = 12;
const FLOATING_CARD_BOTTOM_PAD_DESIGN = 12;
const DOCKED_CARD_Y_OFFSET_DESIGN = 0;
const PINNED_CARD_LIFT_DESIGN    = 18;
// Transparent rows under the card in `supstrate_you.png` (opaque to row 151 of 160).
const PLAYER_CARD_ART_TAIL       = 9 / 160;
// The pinned card's background is this much wider on each side than the rows (its content
// stays put): with the same outline, the antialiased edge of the row under it peeked out
// as a light pixel line along the card's sides and corners.
const PINNED_CARD_BG_OVERFLOW_PX = 4;

/** `PINNED_CARD_BG_OVERFLOW_PX` in whole device px, so the card's content stays on the pixel grid. */
function pinnedCardBgOverflow(): number
{
  const dpr = window.devicePixelRatio || 1;
  return Math.max( 1, Math.round( PINNED_CARD_BG_OVERFLOW_PX * dpr ) ) / dpr;
}
// Space under the list down to the screen bottom: the navigation plus the gap for the jump pill.
// The list top stays put; a taller reserve only shortens the list. The bar's line sits ~128 px
// below the navigation's top, so 430 leaves the 64 px pill ~46 px above and below; at 396 it
// was ~29 and the pill looked squeezed between the player card and the bar.
const LEADERBOARD_NAV_RESERVE_DESIGN = 430;
// Native scroll: the scroller reaches past the list so the sticky player card can stick out of it
// as the floating one does (`FLOATING_CARD_BOTTOM_PAD_DESIGN` above the top; below the bottom, its
// render overflow minus the lift). Rows in these bands are covered by backdrop-coloured masks.
const NATIVE_EXTEND_TOP_DESIGN    = FLOATING_CARD_BOTTOM_PAD_DESIGN;
const NATIVE_EXTEND_BOTTOM_DESIGN = Math.max( 0,
  FLOATING_CARD_BOTTOM_PAD_DESIGN - PINNED_CARD_LIFT_DESIGN + FLOATING_CARD_RENDER_OVERFLOW_DESIGN );

// The bands in whole device px: the row layer then sits on the same device-pixel phase as with
// JS scrolling (a fractional band shifted every row by a device pixel).
function nativeExtendTop(): number
{
  return snapToDevicePx( LayoutScaler.heightFromDesign( NATIVE_EXTEND_TOP_DESIGN ) );
}

function nativeExtendBottom(): number
{
  return snapToDevicePx( LayoutScaler.heightFromDesign( NATIVE_EXTEND_BOTTOM_DESIGN ) );
}

/** The sticky player card inside the native scroller, and the mask under it. */
interface StickyCard
{
  /** Full scroll height; holds the spacer that puts the card at its row, then the card. */
  wrap: HTMLDivElement;
  spacer: HTMLDivElement;
  /** position:sticky, between the top and bottom pins. */
  holder: HTMLDivElement;
  /** Ends at the card's own row: its mask sticks to the list bottom only while the card is pinned there. */
  collapse_wrap: HTMLDivElement;
  collapse_spacer: HTMLDivElement;
}

export class LeaderboardScreen extends UIElement
{
  /** Prepare the shared card art while the preceding screen is still visible. */
  public static prepareRowBackdrop(): Promise<void>
  {
    const scroll_w = Game.getContentWidth() - LayoutScaler.xFromDesign( 10 ) * 2;
    return prepareLeaderboardRowBackdrop(
      Math.min( scroll_w, LayoutScaler.widthFromDesign( 1060 ) ), LayoutScaler.heightFromDesign( ITEM_H_DESIGN )
    );
  }

  /** Avatars and flags of the cached global board, warmed up from the menu before the screen opens. */
  public static cachedBoardImageIds(): ImgID[]
  {
    const ids = new Set<ImgID>();
    const board = LeaderboardBoardService.get();
    const players = board ? buildRowsForCurrentPlayer( board.users ) : [];
    // Only what is on screen at open: warming the whole board would decode every flag in it.
    const warm = players.slice( 0, WARMUP_ROWS );
    const own = players.find( p => p.is_player );
    if ( own && !warm.includes( own ) )
      warm.push( own );
    for ( const player of warm )
    {
      ids.add( AvatarUtils.getAvatarImgId( player.avatar_id ) );
      ids.add( getFlagByCountryCode( player.country_code ) );
    }
    return [ ...ids ];
  }

  private static instance: LeaderboardScreen | null = null;

  /** Debug: the live screen, for `leadersDebug`. */
  public static current(): LeaderboardScreen | null
  {
    return LeaderboardScreen.instance;
  }

  private events_controller: BasicEventsController | null = null;
  private scroll_container: UIScrollableContainerCSS | null = null;
  private player_row_configs: LeaderboardScreenPlayerInfoConfig[] = [];
  private rows_view: LeaderboardRowsView | null = null;
  private rows_backdrop: { url: string; dispose(): void } | null = null;
  private rows_backdrop_pending = false;

  private row_ctx: CanvasRenderingContext2D;
  private bound_first = -1;
  private bound_last = -1;

  /** The browser scrolls the list (`UIScrollableContainerCSS` native mode); see `SCROLL_MODE_KEY`. */
  private native_scroll = nativeScrollEnabled();
  private sticky_card: StickyCard | null = null;

  private active_tab: LeaderboardTab = LeaderboardTab.Global;
  private floating_user_card: LeaderboardScreenPlayerInfo | null = null;
  private floating_card_clip: HTMLDivElement | null = null;
  private floating_card_div: HTMLDivElement | null = null;
  private floating_host_rect: DOMRect | null = null;
  private floating_host_key = "";
  private floating_clip_key = "";
  private floating_card_top = "";
  private pool_hidden = false;
  private floating_card_view_y: number | null = null;
  private current_user_config: LeaderboardScreenPlayerInfoConfig | null = null;
  private current_user_natural_y: number = 0;
  private loading_spinner: Spinner | null = null;
  private spinner_timer: ReturnType<typeof setTimeout> | null = null;
  private players_key = "";
  private unsubscribe_board: ( () => void ) | null = null;
  private jump_button: LeaderboardJumpButton | null = null;
  private jump_visible = false;
  private jump_enabled = false;
  /** The pill is centred against the measured navigation line (`placeJumpButton`). */
  private jump_placed = false;
  /** The full board is shown (not only its head): only then does "Down" mean the real bottom. */
  private board_complete = false;
  private is_updating_floating_card = false;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );
    LeaderboardScreen.instance = this;
    // List rows and the player card are DOM in either UI backend.
    this.row_ctx = createNullCanvasContext( this.ctx );
    this.initializeEventsController( this.ctx.canvas );
    this.createScreenBackground();
    this.createTopBackground();
    this.createTitle();
    this.createInfoButton();
    this.createContent();
    this.createScrollContainer();
    this.createTabsAndLine();
    this.createJumpButton();
    // Every board the services get (menu prefetch, head, full) lands here, also while open;
    // only the active tab's board is shown.
    const unsubscribe = [ LeaderboardBoardService, LeaderboardCountryBoardService ].map( source =>
      source.subscribe( board => { if ( source === this.boardSource() ) this.showBoard( board ); } )
    );
    this.unsubscribe_board = () => unsubscribe.forEach( off => off() );
    this.loadPlayerList();
  }

  /** The board behind the active tab: global, or the player's country. */
  private boardSource(): BoardSource
  {
    return this.active_tab === LeaderboardTab.Country ? LeaderboardCountryBoardService : LeaderboardBoardService;
  }

  /**
   * Shows the best board known right now (this session's, or the previous session's from
   * storage) in the same frame, then refreshes it in the background. Only with nothing known
   * at all does the list wait for the network, and then for the board's head first.
   */
  private loadPlayerList(): void
  {
    const board = this.boardSource().get();
    if ( board )
      this.showBoard( board );
    else
      this.waitForBoard();
    this.refreshBoard();
  }

  private refreshBoard(): void
  {
    const source = this.boardSource();
    source.refreshIfStale().catch( e =>
    {
      console.warn( '[LeaderboardScreen] Failed to load players:', String( e ) );
      // The tab changed meanwhile: that board's own load reports for itself.
      if ( source !== this.boardSource() )
        return;
      // Nothing came at all: stop waiting and show the empty list, as before.
      if ( this.player_row_configs.length === 0 && this.isWaitingForBoard() )
      {
        this.stopWaitingForBoard();
        this.createPlayerInfo( [] );
      }
    } );
  }

  private showBoard( board: BoardSnapshot ): void
  {
    if ( !this.scroll_container )
      return;
    this.stopWaitingForBoard();
    this.board_complete = board.complete;
    this.applyPlayers( buildRowsForCurrentPlayer( board.users ) );
    this.updateJumpButton();
  }

  private createJumpButton(): void
  {
    const button = new LeaderboardJumpButton(
      this.ctx,
      ( this.rect.getW() - LayoutScaler.widthFromDesign( JUMP_BUTTON_W_DESIGN ) ) / 2,
      this.rect.getH() - LayoutScaler.heightFromDesign( JUMP_BUTTON_BOTTOM_OFFSET_DESIGN ),
      LayoutScaler.widthFromDesign( JUMP_BUTTON_W_DESIGN ),
      LayoutScaler.heightFromDesign( JUMP_BUTTON_H_DESIGN ),
    );
    button.setOnClick( () => this.onJumpClick() );
    button.setOpacityImmediate( 1 );
    button.setClickable( false );
    this.addChild( button );
    this.jump_button = button;
    this.jump_visible = true;
  }

  /**
   * Centres the pill between the bottom of the list's last card and the navigation bar's line.
   * The bar lives on its own canvas and its line depends on the aspect ratio and safe areas,
   * so both edges are measured on screen instead of using design offsets.
   */
  private placeJumpButton(): boolean
  {
    const button = this.jump_button;
    const sc = this.scroll_container;
    const line_client_y = navBarLineClientY();
    if ( !button || !sc || line_client_y === null || !this.isDomMode() ) return false;
    // Screen-local y → client y is a plain offset (UI px are CSS px); the pill's own node gives it.
    const node_box = button.getDomNode().getBoundingClientRect();
    // Not laid out yet (the screen is being shown): `update` tries again next frame.
    if ( node_box.height <= 0 ) return false;
    const line_y = line_client_y - ( node_box.top - button.pixelPosImportant().y );
    // Where the pinned player card's art visibly ends (rows end there too): its box bottom, as
    // `positionFloatingCard` puts it, minus the transparent tail at the bottom of the art.
    const card_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const list_bottom = sc.pixelPosImportant().y + sc.getRect().getH()
      + LayoutScaler.heightFromDesign( FLOATING_CARD_BOTTOM_PAD_DESIGN )
      - LayoutScaler.heightFromDesign( PINNED_CARD_LIFT_DESIGN )
      - card_h * PLAYER_CARD_ART_TAIL;
    const h = button.getRect().getH();
    if ( line_y - list_bottom >= h )
    {
      const y = Math.round( ( list_bottom + line_y - h ) / 2 );
      button.setPixelPos( new Vector2( button.pixelPosImportant().x, y ), true );
    }
    return true;
  }

  private onJumpClick(): void
  {
    const sc = this.scroll_container;
    if ( !sc || !this.jump_enabled ) return;
    sc.glideTo( this.jump_button?.getDirection() === "up" ? 0 : sc.getMaxScrollOffset() );
  }

  /**
   * Visible from the first frame; navigation is enabled once the complete board is ready.
   * Points down in the upper half and up in the lower half of a scrollable list.
   */
  private updateJumpButton(): void
  {
    const button = this.jump_button;
    const sc = this.scroll_container;
    if ( !button || !sc ) return;
    const max = sc.getMaxScrollOffset();
    const enabled = this.board_complete && this.rows_backdrop !== null && this.player_row_configs.length > 0 && max > 1;
    button.setDirection( enabled && sc.getScrollOffset() > max / 2 ? "up" : "down" );
    if ( enabled === this.jump_enabled ) return;
    this.jump_enabled = enabled;
    button.setClickable( enabled );
  }

  private waitForBoard(): void
  {
    if ( this.spinner_timer !== null || this.loading_spinner )
      return;
    this.spinner_timer = setTimeout( () =>
    {
      this.spinner_timer = null;
      if ( this.player_row_configs.length === 0 && this.scroll_container )
        this.showLoadingSpinner();
    }, SPINNER_DELAY_MS );
  }

  private isWaitingForBoard(): boolean
  {
    return this.spinner_timer !== null || this.loading_spinner !== null;
  }

  private stopWaitingForBoard(): void
  {
    if ( this.spinner_timer !== null )
    {
      clearTimeout( this.spinner_timer );
      this.spinner_timer = null;
    }
    this.hideLoadingSpinner();
  }

  private createScreenBackground(): void
  {
    const full_x = -Game.safeAreaLeft();
    const full_y = -Game.safeAreaTop();
    const full_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const full_h = this.rect.getH() + Game.safeAreaTop();
    const background = new RoundedRectangle(
      this.ctx,
      full_x,
      full_y,
      full_w,
      full_h,
      0,
      {
        type: "linear-gradient",
        from: "#1052FF",
        to: "#0A3EC7",
        direction: "vertical",
      }
    );

    this.addChild( background );
  }

  private createScrollContainer(): void
  {
    const design_clip_h_overflow      = 20;
    const design_scroll_y             = 466;
    const scroll_y = LayoutScaler.yFromDesign( design_scroll_y );
    const scroll_margin = LayoutScaler.xFromDesign( 10 );
    const scroll_w = this.rect.getW() - scroll_margin * 2;
    const leaderboard_bottom = this.getLeaderboardBottom()
      + LayoutScaler.heightFromDesign( PINNED_CARD_LIFT_DESIGN );
    const scroll_h = Math.max(
      LayoutScaler.heightFromDesign( ITEM_H_DESIGN * 2 ),
      leaderboard_bottom - scroll_y
    );
    const scroll_x = scroll_margin;

    this.scroll_container = new UIScrollableContainerCSS(
      this.ctx,
      scroll_x,
      scroll_y,
      scroll_w,
      scroll_h,
      {
        scroll_speed: 1,
        enable_momentum: true,
        enable_touch_scroll: true,
        scroll_sensitivity: 0.8,
        clip_horizontal_overflow: LayoutScaler.widthFromDesign( design_clip_h_overflow ),
        pre_scroll_viewport_shrink: LayoutScaler.heightFromDesign(
          CARD_H_DESIGN - FLOATING_CARD_BOTTOM_PAD_DESIGN
        ),
        pre_scroll_defer_expand_until_external: true,
        virtual_content: true,
        native_scroll: this.native_scroll,
        native_extend_top: nativeExtendTop(),
        native_extend_bottom: nativeExtendBottom(),
      }
    );

    this.addChild( this.scroll_container! );
    this.scroll_container.setOnClick( ( p ) => this.openProfileFromScrollPoint( p ) );
    this.scroll_container.setOnNativeTap( ( x, y ) => this.onNativeTap( x, y ) );
    if ( this.scroll_container.isNativeScroll() )
      this.createListMasks( this.scroll_container );
    // Native rows and the sticky card scroll entirely in the browser. JS mode rebinds its
    // small pool before moving the floating card in the same frame.
    this.scroll_container.setOnScrollChange( () =>
    {
      if ( !this.native_scroll )
        this.syncVisibleRows();
      this.updateFloatingCardPosition();
      this.updateJumpButton();
    } );
    this.scroll_container.updateDOMPosition();
  }

  private createInfoButton(): void
  {
    const design_btn_size         = 120;
    const design_btn_margin_right = 33;
    const design_btn_y            = 105;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_w - LayoutScaler.xFromDesign( design_btn_margin_right );
    const btn_y = LayoutScaler.yFromDesign( design_btn_y );

    const info_icon = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_LeaderboardInfoButton,
      {},
      true
    );

    info_icon.setOnClick( () =>
    {
      OverlayFactory.openOverlay( OverlayId.LEADERBOARD_INFO_OVERLAY );
    } );

    this.addChild( info_icon );
  }

  private createTitle(): void
  {
    const design_title_h    = 106;
    const design_title_y    = 112;
    const design_text_size  = 80;

    const title_w = this.rect.getW();
    const title_h = LayoutScaler.heightFromDesign( design_title_h );
    const title_x = this.rect.getW2() - title_w / 2;
    const title_y = LayoutScaler.yFromDesign( design_title_y );

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        // Same text style as the settings dialog title.
        localization_key: "LEADERBOARD",
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 20 ),
        outline_color: "#FDA218",
        shadow_color: "#0D0448",
        shadow_blur: LayoutScaler.widthFromDesign( 1 ),
        shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 5 ),
      }
    );
    this.addChild( title );
  }

  private createTabsAndLine(): void
  {
    const tabs_bg_x = LayoutScaler.xFromDesign( 27 );
    const tabs_bg_w = this.rect.getW() - tabs_bg_x * 2;
    const tabs_bg = new RoundedRectangle(
      this.ctx,
      tabs_bg_x,
      LayoutScaler.yFromDesign( 286 ),
      tabs_bg_w,
      LayoutScaler.heightFromDesign( 138 ),
      LayoutScaler.cornerRadiusFromDesign( 39 ),
      "#0A1E60"
    );
    this.addChild( tabs_bg );

    const design_header_h  = 116;
    // Tabs are 120px high inside the 138px background: 9px top/bottom.
    // LeaderboardScreenTabs adds its own 20px local Y offset.
    const design_header_y  = 275;

    const header_w = tabs_bg_w;
    const header_h = LayoutScaler.heightFromDesign( design_header_h );
    const header_x = tabs_bg_x;
    const header_y = LayoutScaler.yFromDesign( design_header_y );

    const header = new LeaderboardScreenTabs(
      this.ctx, header_x, header_y, header_w, header_h,
      ( tab: LeaderboardTab ) => { this.refreshPlayerList( tab ); }
    );
    this.addChild( header );
  }

  private createTopBackground(): void
  {
    const full_x = -Game.safeAreaLeft();
    const full_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const top_bg = new UINineSliceElement(
      this.ctx,
      full_x,
      LayoutScaler.yFromDesign( 247 ),
      full_w,
      LayoutScaler.heightFromDesign( 206 ),
      ImgID.UI_LeaderboardTopBg,
      "leaderboard_top_bg"
    );

    this.addChild( top_bg );
  }

  private createContent(): void
  {
    // Full physical screen width (edge to edge, including safe areas).
    const full_x = -Game.safeAreaLeft();
    const full_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();

    // Start exactly below the active Global tab (Y260 + H101), so the selected
    // tab visually merges into the leaderboard content without a blue gap.
    const backdrop_y = LayoutScaler.yFromDesign( 453 );
    const backdrop = new RoundedRectangle(
      this.ctx,
      full_x,
      backdrop_y,
      full_w,
      Math.max( 0, this.rect.getH() - backdrop_y ),
      0,
      ColorScheme.leaderboard_content_backdrop,
    );
    this.addChild( backdrop );

  }

  private getLeaderboardBottom(): number
  {
    return Math.max( 0, this.rect.getH() - LayoutScaler.heightFromDesign( LEADERBOARD_NAV_RESERVE_DESIGN ) );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
    {
      this.events_controller.cleanup();
    }

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  private isBlockedByModal(): boolean
  {
    return DialogManager.isAnyDialogOpened() || OverlayManager.isInteractionBlocked();
  }

  private refreshPlayerList( tab: LeaderboardTab ): void
  {
    this.active_tab = tab;
    this.clearPlayerList();
    this.loadPlayerList();
  }

  private clearPlayerList(): void
  {
    this.destroyFloatingCard();
    this.current_user_config    = null;
    this.current_user_natural_y = 0;

    this.player_row_configs = [];
    this.players_key = "";
    this.board_complete = false;
    this.rows_view?.clear();
    this.bound_first = -1;
    this.bound_last = -1;
    this.scroll_container?.clearScrollableChildren();
    this.updateJumpButton();
  }

  public canReuseScreen(): boolean
  {
    return this.scroll_container !== null;
  }

  public onScreenHidden(): void
  {
    this.pool_hidden = true;
    this.events_controller?.disable();
    this.scroll_container?.hideElement( 0 );
    if ( this.floating_card_clip )
      this.floating_card_clip.style.display = 'none';
  }

  public onScreenShown(): void
  {
    this.pool_hidden = false;
    this.floating_host_rect = null;
    this.events_controller?.enable();
    this.scroll_container?.showElement( 0 );
    if ( this.floating_card_clip )
      this.floating_card_clip.style.display = '';
    // The navigation can be rebuilt (resize, safe areas) while the screen is away.
    this.jump_placed = false;
    // The board on screen is already the latest the service has (it is subscribed); this only
    // refetches when it is older than the throttle or the player's own score changed.
    this.refreshBoard();
  }

  /** Rebuilds the list for a new board; a board equal to the shown one changes nothing. */
  private applyPlayers( players: LeaderboardScreenPlayerInfoConfig[] ): void
  {
    const key = playersKey( players );
    if ( key === this.players_key )
      return;
    // Keep the scroll position: a refresh landing mid-scroll used to throw the list to the top,
    // and the full board arriving after its head must extend the list under the finger.
    const restore_offset = this.players_key ? ( this.scroll_container?.getScrollOffset() ?? 0 ) : null;
    this.players_key = key;
    this.createPlayerInfo( players, restore_offset );
  }

  /** Debug: shows the given board as if the server returned it (`leadersDebug.fake`). */
  public debugShowPlayers( users: BoardUser[] ): void
  {
    this.stopWaitingForBoard();
    this.board_complete = true;
    this.applyPlayers( buildRowsForCurrentPlayer( users ) );
    this.updateJumpButton();
  }

  /** Debug: jumps the list so the row at `index` is on top (`leadersDebug.scroll`). */
  public debugScrollToIndex( index: number ): void
  {
    this.scroll_container?.scrollTo( this.rowY( Math.max( 0, index ) ), false );
  }

  /** Debug: taps the Down/Up pill (`leadersDebug.jump`). */
  public debugJump(): void
  {
    this.onJumpClick();
  }

  /** Debug: what the list holds right now (`leadersDebug.stats`). */
  public debugStats(): Record<string, unknown>
  {
    const layer = this.scroll_container?.getContentLayer();
    return {
      scroll_mode: this.scroll_container?.isNativeScroll() ? "native" : "js",
      row_rendering: this.native_scroll ? "static" : "virtual",
      players: this.player_row_configs.length,
      rows_built: this.rows_view?.builtRowCount ?? 0,
      bind_pending: false,
      rows_bound: this.rows_view?.boundRowCount ?? 0,
      bound_range: [ this.bound_first, this.bound_last ],
      layer_nodes: layer ? layer.getElementsByTagName( "*" ).length : 0,
      layer_canvases: layer ? layer.getElementsByTagName( "canvas" ).length : 0,
      scroll_offset: this.scroll_container?.getScrollOffset() ?? 0,
      board: ( () =>
      {
        const board = this.boardSource().get();
        return board ? { users: board.users.length, complete: board.complete, live: board.live, age_s: Math.round( ( Date.now() - board.fetched_at ) / 1000 ) } : null;
      } )(),
      board_loading: this.boardSource().isLoading(),
      spinner: this.loading_spinner !== null,
      jump: this.jump_visible ? this.jump_button?.getDirection() : null,
    };
  }

  private showLoadingSpinner(): void
  {
    if ( this.loading_spinner ) return;

    const design_spinner_size = 120;
    const spinner_size = LayoutScaler.widthFromDesign( design_spinner_size );
    const scroll_rect = this.scroll_container?.getRect();
    const scroll_pos = this.scroll_container?.pixelPosImportant();
    const spinner_x = scroll_rect && scroll_pos
      ? scroll_pos.x + ( scroll_rect.getW() - spinner_size ) / 2
      : this.rect.getW2() - spinner_size / 2;
    const spinner_y = scroll_rect && scroll_pos
      ? scroll_pos.y + ( scroll_rect.getH() - spinner_size ) / 2
      : this.rect.getH2() - spinner_size / 2;

    this.loading_spinner = new Spinner(
      this.ctx,
      spinner_x,
      spinner_y,
      spinner_size,
      spinner_size
    );
    this.addChild( this.loading_spinner );
  }

  private hideLoadingSpinner(): void
  {
    const spinner = this.loading_spinner;
    if ( !spinner ) return;
    this.loading_spinner = null;

    // Fades out under the rows that just appeared instead of vanishing in one frame.
    spinner.hideElement( SPINNER_FADE_MS );
    setTimeout( () =>
    {
      spinner.stop();
      this.removeChild( spinner );
    }, SPINNER_FADE_MS + 50 );
  }

  private createPlayerInfo( players: LeaderboardScreenPlayerInfoConfig[], restore_offset: number | null = null ): void
  {
    if ( !this.scroll_container ) return;

    // Rows come sorted by rank from buildPlayerRows().
    this.player_row_configs = players;
    const own_index = players.findIndex( p => p.is_player );
    this.current_user_config = own_index >= 0 ? players[own_index] : null;
    this.current_user_natural_y = own_index >= 0 ? this.rowY( own_index ) : Number.POSITIVE_INFINITY;

    this.bound_first = -1;
    this.bound_last = -1;
    const content_h = players.length > 0
      ? this.rowY( players.length )
      : this.scroll_container.getRect().getH();
    this.scroll_container.setContentHeight( content_h, this.rowStep(), this.rowTopPad() );
    if ( restore_offset !== null )
      this.scroll_container.restoreScrollOffset( restore_offset );
    this.syncVisibleRows();
    if ( this.rows_backdrop ) this.syncFloatingCard();
    this.updateJumpButton();
  }

  /** Shared artwork and a few text/image nodes per row keep scroll binding cheap. */
  private renderRows( first: number, last: number ): void
  {
    const sc = this.scroll_container;
    if ( !sc ) return;
    const width = this.rowW();
    const height = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    this.rows_backdrop ??= acquireLeaderboardRowBackdrop( width, height );
    if ( this.rows_backdrop )
    {
      this.rows_view ??= new LeaderboardRowsView( sc.getContentLayer() );
      this.rows_view.render( this.player_row_configs, {
        width, height, step: this.rowStep(), x: this.rowX(), top_pad: this.rowTopPad(),
      }, this.rows_backdrop.url, this.native_scroll ? undefined : { first, last } );
      return;
    }
    if ( this.rows_backdrop_pending ) return;
    this.rows_backdrop_pending = true;
    void createLeaderboardRowBackdrop( width, height )
      .then( backdrop =>
      {
        if ( this.scroll_container !== sc )
        {
          backdrop.dispose();
          return;
        }
        this.rows_backdrop = backdrop;
        this.bound_first = -1;
        this.bound_last = -1;
        this.syncVisibleRows();
        this.syncFloatingCard();
        this.updateJumpButton();
      } )
      .catch( e => console.warn( "[LeaderboardScreen] row backdrop failed:", String( e ) ) )
      .finally( () => { this.rows_backdrop_pending = false; } );
  }

  /**
   * The player card follows the board without being recreated: a new board (the full one after
   * its head, a background refresh) only updates what changed in it, so it never blinks.
   */
  private syncFloatingCard(): void
  {
    if ( !this.current_user_config )
    {
      this.destroyFloatingCard();
      return;
    }
    if ( !this.floating_user_card )
    {
      this.createFloatingCard();
      return;
    }
    this.floating_user_card.setConfig( this.current_user_config );
    this.floating_user_card.render();
    if ( this.sticky_card )
      this.placeStickyCard( this.sticky_card );
    this.updateFloatingCardPosition();
  }

  /**
   * Native scroll: backdrop-coloured masks over the bands the scroller reaches past the list
   * (`native_extend_*`), so rows are cut at the list edges as before while the sticky card can
   * stick out over them. Each mask sticks in a wrapper as tall as the scroll content.
   */
  private createListMasks( sc: UIScrollableContainerCSS ): void
  {
    const layer = sc.getStickyLayer();
    const band = ( height: number, edge: "top" | "bottom" ) =>
    {
      if ( height <= 0 ) return;
      const wrap = document.createElement( "div" );
      wrap.style.cssText = [
        "position:absolute", "left:0", "top:0", "width:100%", "height:100%", "pointer-events:none",
        "display:flex", "flex-direction:column", `justify-content:${edge === "top" ? "flex-start" : "flex-end"}`,
      ].join( ";" );
      // A pixel past the scroller edge: on a fractional device pixel the mask's antialiased outer
      // edge let the rows under it show through as a thin line; the clip cuts that edge off.
      const mask = document.createElement( "div" );
      mask.style.cssText = [
        "position:sticky", `${edge}:-1px`, `margin-${edge}:-1px`, "flex:none", "width:100%", `height:${height + 1}px`, "z-index:1",
        `background:${ColorScheme.leaderboard_content_backdrop}`,
      ].join( ";" );
      wrap.appendChild( mask );
      layer.appendChild( wrap );
    };
    band( nativeExtendTop(), "top" );
    band( nativeExtendBottom(), "bottom" );
  }

  /**
   * Native scroll: the player card is `position: sticky` inside the scroller, so the compositor
   * moves it together with the rows: docked in its row, pinned to the list top above the view
   * and to the bottom below it (`positionFloatingCard` does the same in JS for JS scrolling).
   * While it is pinned to the bottom, a mask under it hides the rows below it, as the collapsed
   * pre-scroll viewport does in JS scrolling.
   */
  private createStickyCard( sc: UIScrollableContainerCSS, card_w: number, card_h: number, render_h: number, ov: number ): HTMLDivElement
  {
    const ext_top = nativeExtendTop();
    const ext_bottom = nativeExtendBottom();
    const inset = LayoutScaler.heightFromDesign( FLOATING_CARD_BOTTOM_PAD_DESIGN );
    const lift = LayoutScaler.heightFromDesign( PINNED_CARD_LIFT_DESIGN );
    const shrink = LayoutScaler.heightFromDesign( CARD_H_DESIGN - FLOATING_CARD_BOTTOM_PAD_DESIGN );
    const layer = sc.getStickyLayer();
    const column = ( height: string ) =>
    {
      const el = document.createElement( "div" );
      el.style.cssText = [ "position:absolute", "left:0", "top:0", "width:100%", `height:${height}`, "pointer-events:none" ].join( ";" );
      return el;
    };

    const collapse_wrap = column( "0px" );
    const collapse_spacer = document.createElement( "div" );
    const collapse_mask = document.createElement( "div" );
    // Down to the scroller bottom over the bottom band's mask: two masks meeting at the list
    // bottom left a seam there on a fractional device pixel, and the rows showed through it.
    collapse_mask.style.cssText = [
      "position:sticky", "bottom:-1px", "width:100%", `height:${shrink + ext_bottom + 1}px`, "z-index:1",
      `background:${ColorScheme.leaderboard_content_backdrop}`,
    ].join( ";" );
    collapse_wrap.append( collapse_spacer, collapse_mask );

    const wrap = column( "100%" );
    const spacer = document.createElement( "div" );
    const holder = document.createElement( "div" );
    // Pins as in `positionFloatingCard`: the top one `inset` above the list, the bottom one `lift`
    // above the list bottom with the card reaching `inset` below it.
    holder.style.cssText = [
      "position:sticky",
      `top:${ext_top - inset}px`,
      `bottom:${ext_bottom + lift - inset}px`,
      `height:${card_h}px`,
      `width:${card_w + ov * 2}px`,
      `margin-left:${snapToDevicePx( sc.getClipOverflowX() + this.rowX() - ov )}px`,
      "z-index:2",
      "pointer-events:none",
    ].join( ";" );
    const div = document.createElement( "div" );
    div.style.cssText = [ "position:absolute", `left:${ov}px`, "top:0", `width:${card_w}px`, `height:${render_h}px` ].join( ";" );
    holder.appendChild( div );
    wrap.append( spacer, holder );

    layer.append( collapse_wrap, wrap );
    this.sticky_card = { wrap, spacer, holder, collapse_wrap, collapse_spacer };
    this.placeStickyCard( this.sticky_card );
    return div;
  }

  /** Puts the sticky card (and the end of its mask's wrapper) at the player's row. */
  private placeStickyCard( card: StickyCard ): void
  {
    const ext_top = nativeExtendTop();
    // The collapse mask's height (`createStickyCard`), its pixel past the scroller included.
    const mask_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN - FLOATING_CARD_BOTTOM_PAD_DESIGN ) + nativeExtendBottom() + 1;
    const card_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const item_h = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    // The docked position of `positionFloatingCard`, in scroller coordinates.
    const dock_y = this.current_user_natural_y - ( card_h - item_h ) / 2
      - LayoutScaler.heightFromDesign( DOCKED_CARD_Y_OFFSET_DESIGN );
    const top = Math.max( 0, ext_top + dock_y );
    card.spacer.style.height = `${top}px`;
    card.collapse_wrap.style.height = `${top + card_h}px`;
    // The mask ends where the card does: docked, the card hides it; pinned, it sticks to the bottom.
    card.collapse_spacer.style.height = `${Math.max( 0, top + card_h - mask_h )}px`;
  }

  /** Native scroll: where the sticky card is now, in the list's own coordinates (y = 0 at its top). */
  private stickyCardViewY(): number | null
  {
    const sc = this.scroll_container;
    if ( !this.sticky_card || !sc ) return null;
    const box = this.sticky_card.holder.getBoundingClientRect();
    if ( box.height === 0 ) return null;
    const host = sc.getOverlayHost().getBoundingClientRect();
    return box.top - host.top - nativeExtendTop();
  }

  private rowStep(): number
  {
    return LayoutScaler.heightFromDesign( ITEM_H_DESIGN ) + LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
  }

  private rowTopPad(): number
  {
    return LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
  }

  private rowY( index: number ): number
  {
    return this.rowTopPad() + index * this.rowStep();
  }

  /** JS mode binds the new viewport before the same frame presents its offset. */
  private syncVisibleRows(): void
  {
    const sc = this.scroll_container;
    const n = this.player_row_configs.length;
    if ( !sc ) return;
    const { first, last } = this.native_scroll ? { first: 0, last: n - 1 } : visibleRowRange(
      sc.getScrollOffset(), sc.getRect().getH(), this.rowStep(), this.rowTopPad(), n, ROWS_BUFFER
    );
    if ( first === this.bound_first && last === this.bound_last ) return;
    this.bound_first = first;
    this.bound_last = last;
    this.renderRows( first, last );
  }

  private rowW(): number
  {
    return Math.min(
      this.scroll_container?.getRect().getW() ?? 0,
      LayoutScaler.widthFromDesign( 1060 )
    );
  }

  private rowX(): number
  {
    return ( ( this.scroll_container?.getRect().getW() ?? 0 ) - this.rowW() ) / 2;
  }

  private destroyFloatingCard(): void
  {
    if ( this.sticky_card )
    {
      this.sticky_card.wrap.remove();
      this.sticky_card.collapse_wrap.remove();
      this.sticky_card = null;
    }
    this.floating_user_card    = null;
    this.floating_card_div     = null;
    this.floating_card_clip?.remove();
    this.floating_card_clip    = null;
    this.floating_card_view_y  = null;
    // The cached style keys belong to the removed elements. Left as they were, a recreated
    // card with the same geometry never got left/top/height, so its clip stayed 0px high
    // and the player's own row was an empty gap after a refresh.
    this.floating_clip_key     = "";
    this.floating_card_top     = "";
    this.floating_host_key     = "";
    this.floating_host_rect    = null;
  }

  private createFloatingCard(): void
  {
    if ( !this.current_user_config || !this.scroll_container ) return;

    const card_w = Math.min(
      this.scroll_container.getRect().getW(),
      LayoutScaler.widthFromDesign( 1060 )
    );
    const card_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const render_h = card_h + LayoutScaler.heightFromDesign( FLOATING_CARD_RENDER_OVERFLOW_DESIGN );
    // Room for the background that sticks out past the rows (`PINNED_CARD_BG_OVERFLOW_PX`).
    const ov = pinnedCardBgOverflow();

    if ( this.scroll_container.isNativeScroll() )
    {
      const holder = this.createStickyCard( this.scroll_container, card_w, card_h, render_h, ov );
      this.floating_user_card = new LeaderboardScreenPlayerInfo(
        this.row_ctx, 0, 0, card_w, card_h, this.current_user_config, ov
      );
      holder.appendChild( this.floating_user_card.getDomNode() );
      this.floating_user_card.render();
      return;
    }

    // Clip wrapper mirrors the scroll container's own overflow:hidden clip, so the
    // floating card gets progressively clipped at the same top/bottom line as the
    // regular rows instead of popping visible/hidden abruptly.
    const clip = document.createElement( 'div' );
    clip.style.cssText = [
      'position:absolute', 'pointer-events:none', 'z-index:11', 'overflow:hidden',
      `width:${card_w + ov * 2}px`,
    ].join( ';' );

    const div = document.createElement( 'div' );
    div.style.cssText = [
      'position:absolute', `left:${ov}px`,
      `width:${card_w}px`, `height:${render_h}px`,
    ].join( ';' );

    clip.appendChild( div );
    // Keep the pinned row outside the scroll clip. It must overlap the panel at
    // both edges, while the regular leaderboard rows remain clipped.
    document.body.appendChild( clip );

    this.floating_card_clip  = clip;
    this.floating_card_div   = div;

    // A DOM row like the list ones; a canvas here was ~0.7 MB on a 1080 px screen.
    this.floating_user_card = new LeaderboardScreenPlayerInfo(
      this.row_ctx, 0, 0, card_w, card_h, this.current_user_config, ov
    );
    div.appendChild( this.floating_user_card.getDomNode() );

    if ( this.pool_hidden ) clip.style.display = 'none';

    this.floating_user_card.render();
    this.updateFloatingCardPosition();
  }

  private updateFloatingCardPosition(): void
  {
    if ( !this.scroll_container || !this.floating_card_div || !this.floating_card_clip ) return;
    // Collapsing/expanding the viewport can re-enter through the scroll-change callback.
    if ( this.is_updating_floating_card ) return;
    this.is_updating_floating_card = true;
    try
    {
      this.positionFloatingCard( this.scroll_container, this.floating_card_div, this.floating_card_clip );
    }
    finally
    {
      this.is_updating_floating_card = false;
    }
  }

  private positionFloatingCard(
    scroll_container: UIScrollableContainerCSS,
    floating_card_div: HTMLDivElement,
    floating_card_clip: HTMLDivElement,
  ): void
  {
    const card_h   = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const inset    = LayoutScaler.heightFromDesign( FLOATING_CARD_BOTTOM_PAD_DESIGN );
    const render_overflow = LayoutScaler.heightFromDesign( FLOATING_CARD_RENDER_OVERFLOW_DESIGN );
    const sc_h     = scroll_container.getRect().getH();

    // The decorative player substrate is taller than a regular row and is meant
    // to overlap the bottom edge of the list slightly. Keep that tail below the
    // viewport instead of adding an empty inset above the navigation.
    const fixed_y  = sc_h - card_h + inset
      - LayoutScaler.heightFromDesign( PINNED_CARD_LIFT_DESIGN );

    const scroll_y = scroll_container.getScrollOffset();
    const natural_y = this.current_user_natural_y - scroll_y;

    const item_h = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    const dock_y = natural_y
      - ( card_h - item_h ) / 2
      - LayoutScaler.heightFromDesign( DOCKED_CARD_Y_OFFSET_DESIGN );

    // Use a symmetric outward overlap at both edges. This gives the top-pinned
    // state the same visual treatment as the original bottom-pinned state.
    const top_y = -inset;
    const target_y = Math.max( top_y, Math.min( dock_y, fixed_y ) );
    const is_docked_to_bottom = dock_y >= fixed_y;

    if ( is_docked_to_bottom )
      scroll_container.collapsePreScrollViewportIfNeeded();
    else
      scroll_container.expandPreScrollViewportIfNeeded();

    const host = scroll_container.getOverlayHost();
    const host_key = `${host.style.left}|${host.style.top}|${host.style.width}|${host.style.height}`;
    if ( !this.floating_host_rect || host_key !== this.floating_host_key )
    {
      const rect = host.getBoundingClientRect();
      if ( rect.width === 0 )
        return;
      this.floating_host_key = host_key;
      this.floating_host_rect = rect;
    }
    const host_rect = this.floating_host_rect;
    const host_w = host_rect.width;
    const css_x = ( host_w - scroll_container.getRect().getW() ) / 2
      + ( scroll_container.getRect().getW() - this.floating_user_card!.getRect().getW() ) / 2;

    // The card is a pre-rendered canvas: on a fractional CSS offset the browser resamples
    // it and the text comes out soft and thin (pinned to the bottom it usually was, while
    // following its row it happened to land on whole pixels and looked right).
    const clip_left = snapToDevicePx( host_rect.left + css_x - pinnedCardBgOverflow() );
    const clip_top = snapToDevicePx( host_rect.top - inset );
    const clip_key = `${clip_left}|${clip_top}|${sc_h + inset * 2 + render_overflow}`;
    if ( clip_key !== this.floating_clip_key )
    {
      this.floating_clip_key = clip_key;
      floating_card_clip.style.left   = `${clip_left}px`;
      floating_card_clip.style.top    = `${clip_top}px`;
      floating_card_clip.style.height = `${sc_h + inset * 2 + render_overflow}px`;
    }

    const card_top = `${snapToDevicePx( target_y + inset )}px`;
    if ( card_top !== this.floating_card_top )
    {
      this.floating_card_top = card_top;
      floating_card_div.style.top = card_top;
    }
    this.floating_card_view_y = target_y;
  }

  private openProfileFromScrollPoint( p: Vector2 ): void
  {
    if ( !this.scroll_container ) return;
    this.openProfileAtViewY( p.y - this.scroll_container.getAbsolutePosition().y );
  }

  /**
   * Native scroll: a touch tap on the list (the scroller sits above the screen canvas, so the
   * screen's own events never see it). The jump pill can lie under the scroller's bottom band.
   */
  private onNativeTap( view_x: number, view_y: number ): void
  {
    if ( this.isBlockedByModal() || !this.scroll_container ) return;
    const button = this.jump_button;
    if ( button && this.jump_visible )
    {
      const sc_pos = this.scroll_container.pixelPosImportant();
      const x = sc_pos.x + view_x;
      const y = sc_pos.y + view_y;
      if ( button.containsTap( new Vector2( x, y ) ) )
      {
        this.onJumpClick();
        return;
      }
    }
    this.openProfileAtViewY( view_y );
  }

  private openProfileAtViewY( view_y: number ): void
  {
    if ( OverlayManager.isInteractionBlocked() )
      return;

    if ( !this.scroll_container || this.player_row_configs.length === 0 )
      return;

    const item_h   = LayoutScaler.heightFromDesign( ITEM_H_DESIGN );
    const item_gap = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
    const top_pad  = LayoutScaler.heightFromDesign( ITEM_GAP_DESIGN );
    const row_step = item_h + item_gap;

    const card_h = LayoutScaler.heightFromDesign( CARD_H_DESIGN );
    const pinned_y = this.sticky_card ? this.stickyCardViewY() : this.floating_card_view_y;

    if ( this.current_user_config && pinned_y !== null && view_y >= pinned_y && view_y <= pinned_y + card_h )
    {
      this.openProfileForConfig( this.current_user_config );
      return;
    }

    // The scroller's bands past the list are masked: no row shows there.
    if ( view_y < 0 || view_y > this.scroll_container.getRect().getH() )
      return;

    const local_y = view_y + this.scroll_container.getScrollOffset();
    const row_index = Math.floor( ( local_y - top_pad ) / row_step );
    const row_y = top_pad + row_index * row_step;

    if ( row_index < 0 || row_index >= this.player_row_configs.length )
      return;

    if ( local_y < row_y || local_y > row_y + item_h )
      return;

    this.openProfileForConfig( this.player_row_configs[row_index] );
  }

  // Profile dialog isn't ported yet — tapping a row is a no-op for now.
  private openProfileForConfig( config: LeaderboardScreenPlayerInfoConfig ): void
  {
    LeaderboardProfileDialog.openFor( config );
  }


  public destroy(): void
  {
    if ( LeaderboardScreen.instance === this )
      LeaderboardScreen.instance = null;
    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
    // Pool rows live in the container's layer and go with it.
    this.rows_view?.destroy();
    this.rows_view = null;
    this.rows_backdrop?.dispose();
    this.rows_backdrop = null;
    this.scroll_container?.destroy();
    this.scroll_container = null;
    this.bound_first = -1;
    this.bound_last = -1;
    this.player_row_configs = [];
    this.unsubscribe_board?.();
    this.unsubscribe_board = null;
    this.stopWaitingForBoard();
    this.destroyFloatingCard();
    this.current_user_config    = null;
    this.current_user_natural_y = 0;
  }

  public name(): string
  {
    return "LeaderboardScreen";
  }

  private renderFloatingCard(): void
  {
    // DOM card: render() only touches what changed, and is a no-op when nothing did.
    this.floating_user_card?.render();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.renderFloatingCard();

    if ( this.dom_mode )
    {
      this.setDomBackdrop( ColorScheme.leaderboard_bg_solid );
      this.startRender();
      this.finishRender();
      SafeAreas.syncBothSafeAreaOverlaysDom( this.getDomHost() );
      return;
    }

    this.startRender();

    this.ctx.save();
    this.ctx.translate( -this.rect.getW2(), -this.rect.getH2() );

    const bg_x = -Game.safeAreaLeft();
    const bg_y = -Game.safeAreaTop();
    const bg_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const bg_h = this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom();
    this.ctx.fillStyle = ColorScheme.leaderboard_bg_solid;
    this.ctx.fillRect( bg_x, bg_y, bg_w, bg_h );

    this.ctx.restore();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    if ( this.scroll_container )
      this.scroll_container.update( now );
    this.updateFloatingCardPosition();
    if ( !this.jump_placed && this.jump_visible )
      this.jump_placed = this.placeJumpButton();
  }
}

function buildRowsForCurrentPlayer( users: BoardUser[] ): LeaderboardScreenPlayerInfoConfig[]
{
  const parsed_avatar = parseInt( localStorage.getItem( 'player_avatar_id' ) ?? '0', 10 );
  return buildPlayerRows( users, {
    id: getLeaderboardCurrentUserId(),
    avatar_id: isNaN( parsed_avatar ) ? 0 : parsed_avatar,
    facebook_av_url: localStorage.getItem( 'player_facebook_avatar_url' ),
    days_in_game: ownDaysInGame( StatisticsStorage.load().install_date ),
  } );
}
